import React, { useEffect, useRef, useState, useContext, useMemo, useCallback } from 'react';

import {
  Box,
  Button,
  Grid,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { BsCheck } from 'react-icons/bs';
import { FiSend } from 'react-icons/fi';
import { BiError, BiExport } from 'react-icons/bi';
import { AiOutlineRedo } from 'react-icons/ai';
import { AxiosResponse } from 'axios';

import clsx from 'clsx';

import { PayScheduleInfo, DownloadBACS } from '@vyce/core/src/views/payroll/components';
import { AppSearchInput, AppMenu } from '@vyce/core/src/components/inputs';

import {
  Company,
  FilterSection,
  PAY_PERMISSIONS,
  PayrunLine,
  PayrunPayload,
  PayrunTotals,
  PaySchedule,
  PayslipsSendStarted,
  PeriodData,
  TableHovers,
  VAT_TREATMENTS,
} from '@vyce/core/src/types';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { applyColumnHovers, isOutsourcedSchedule } from '@vyce/core/src/utils/payroll';
import {
  GetPayrun,
  MarkAsPaidRequest,
  SendBulkPayStatements,
  SendDocumentRequest,
  SendPayslipsRequest,
} from '@vyce/core/src/api/types';
import {
  currencyFormatter,
  generateExcelFileLink,
  getBooleanFilterValue,
  getBusinessTypesFilterValue,
  openPresignedFile,
} from '@vyce/core/src/utils';
import { getUrlItems } from '@vyce/core/src/utils/url';
import {
  DOCUMENT_STATUSES_ARR,
  GRID_PAGE_SIZE,
  RTW_FILTER_OPTIONS,
  SUBCONTRACTOR_TYPES,
} from '@vyce/core/src/constants';
import {
  AppIconButton,
  AppLink,
  FilterSystem,
  GridActions,
  ConfirmDialog,
  Hint,
  AppDataGrid,
  IsVerified,
} from '@vyce/core/src/components';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { DeviceContext } from '@vyce/core/src/contexts';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

import { SendPayStatementsDialog } from './components';
import { DRCVatCell, ReverseChargePill, VatAmountCell } from './components/VatTreatmentCells';
import { useStyles } from './styles';

interface Props {
  refreshPayrunRequest: Function;
  getPayrunRequest: (params: GetPayrun) => Promise<AxiosResponse>;
  markPayrunAsPaidRequest: (params: MarkAsPaidRequest) => Promise<AxiosResponse>;
  markPayrunAsPaidForEmployeeRequest: (params: MarkAsPaidRequest) => Promise<AxiosResponse>;
  sendPayslipsByEmailRequest: (params: SendPayslipsRequest) => Promise<AxiosResponse<PayslipsSendStarted>>;
  sendPayslipsBySMSRequest: (params: SendPayslipsRequest) => Promise<AxiosResponse<PayslipsSendStarted>>;
  sendPassportRequest: (params: SendDocumentRequest) => Promise<AxiosResponse>;
  sendShareCodeRequest: (params: SendDocumentRequest) => Promise<AxiosResponse>;
  sendPayslipsToEmployeesBySMSRequest: (
    params: SendBulkPayStatements
  ) => Promise<AxiosResponse<PayslipsSendStarted>>;
  sendPayslipsToEmployeesByEmailRequest: (
    params: SendBulkPayStatements
  ) => Promise<AxiosResponse<PayslipsSendStarted>>;
  downloadPayrunBACSRequest: Function;
  downloadPayrunsBACSRequest: Function;
  getPayrunAsFileRequest: Function;
  token: string;
}

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'asc' }];

/** DRC amounts arrive as decimal strings, split into what has been paid and what has not. */
const sumDrcVat = (paid?: string, unpaid?: string) => Number(paid || 0) + Number(unpaid || 0);

/**
 * A send answers 202 — accepted, not delivered. The sends run in the background and there is
 * no progress route, so the toast says what is happening, never that it is done.
 */
const sendingMessage = (linesPending: number) =>
  `Sending ${linesPending} ${linesPending === 1 ? 'statement' : 'statements'}`;

export const Payrun: React.FC<Props> = ({
  refreshPayrunRequest,
  getPayrunRequest,
  markPayrunAsPaidRequest,
  markPayrunAsPaidForEmployeeRequest,
  downloadPayrunBACSRequest,
  downloadPayrunsBACSRequest,
  getPayrunAsFileRequest,
  sendPayslipsByEmailRequest,
  sendPayslipsBySMSRequest,
  sendPassportRequest,
  sendShareCodeRequest,
  token,
  sendPayslipsToEmployeesBySMSRequest,
  sendPayslipsToEmployeesByEmailRequest,
}) => {
  const classes = useStyles();
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const horizontalScrollStyles = useHorizontalScrollStyles();
  const [totals, setTotals] = useState<PayrunTotals>();
  const [hovers, setHovers] = useState<TableHovers>();
  const [rows, setRows] = useState<PayrunLine[]>([]);
  const [paySchedule, setPaySchedule] = useState<PaySchedule>();
  const [status, setStatus] = useState<string>();
  const [companyId, setCompanyId] = useState<string>('');
  const [company, setCompany] = useState<Company>();
  const [loading, setLoading] = useState<boolean>(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [markingAsPaid, setMarkingAsPaid] = useState<boolean>(false);
  const [openSendPayStatementsDialog, setOpenSendPayStatementsDialog] = useState<boolean>(false);
  const [payrunId, setPayrunId] = useState<string>('');
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const mountedRef = useRef(true);
  const [periodData, setPeriodData] = useState<PeriodData>();

  const [filters, setFilters] = useState<Partial<PayrunPayload>>();
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);

  // The pay-run read is not documented as carrying the reverse-charge fields, so the column is
  // shown only when the rows really have them — otherwise the grid stays exactly as it was.
  const hasReverseCharge = useMemo(() => rows.some(row => !!row.vat_treatment), [rows]);

  // Paid plus unpaid, the same as the lines. The pre-summed total is the fallback for a response
  // that carries only it — the gateway answers a missing half with zero rather than leaving it out.
  const runDrcVat = useMemo(() => {
    if (!totals || (totals.total_drc_vat === undefined && totals.drc_vat_unpaid === undefined)) {
      return undefined;
    }
    return sumDrcVat(totals.drc_vat_paid, totals.drc_vat_unpaid) || Number(totals.total_drc_vat || 0);
  }, [totals]);

  const {
    sortModel,
    offset,
    substring,
    total,
    setTotal,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  const { isMobile } = useContext(DeviceContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isPaid = status === 'paid';
  const { canOnResource, isInternalAdmin } = usePermissions();
  // Pay rights are scoped per pay schedule, and this run belongs to one — a
  // company-wide grant still passes, canOnResource folds that in.
  const payScheduleId = paySchedule?.uuid;
  // Two questions per action: the right, and whether the operation exists on this
  // schedule at all. Vyce processes payments, BACS and payslips for an outsourced
  // schedule, so the company cannot — the owner included. The server answers 409.
  // Legend staff are the side that runs that payroll, so the block is the
  // company's and not theirs: an internal admin keeps every action here.
  const outsourcedBlocksActions = isOutsourcedSchedule(paySchedule) && !isInternalAdmin;
  const canSendPayslips =
    !outsourcedBlocksActions &&
    canOnResource(PAY_PERMISSIONS.SEND_PAYSLIPS, 'pay_schedule', payScheduleId);
  const canSubmitPayRun =
    !outsourcedBlocksActions &&
    canOnResource(PAY_PERMISSIONS.SUBMIT_PAY_RUN, 'pay_schedule', payScheduleId);
  const canGenerateBacs =
    !outsourcedBlocksActions &&
    canOnResource(PAY_PERMISSIONS.GENERATE_BACS, 'pay_schedule', payScheduleId);

  const filtersSections: FilterSection[] = [
    {
      title: 'Checks',
      expanded: true,
      filters: [
        {
          type: 'select',
          multiple: false,
          label: 'ID Verified',
          field: 'id_verified',
          values: ['', 'Verified', 'Not Verified'],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'ID Status',
          field: 'id_status',
          values: ['', ...DOCUMENT_STATUSES_ARR],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'RTW Status',
          field: 'rtw_status',
          values: ['', ...RTW_FILTER_OPTIONS],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'CIS Verified',
          field: 'cis_verified',
          values: ['', 'Verified', 'Not Verified'],
          defaultValue: '',
        },
      ],
    },
    {
      title: 'Payroll Types',
      expanded: true,
      filters: [
        {
          type: 'select',
          multiple: false,
          label: 'Pay Scheme',
          field: 'pay_scheme',
          values: [
            '',
            SUBCONTRACTOR_TYPES.CIS_SELF_EMPLOYED.toUpperCase(),
            SUBCONTRACTOR_TYPES.SELF_EMPLOYED.toUpperCase(),
            SUBCONTRACTOR_TYPES.PAYE.toUpperCase(),
          ],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'Taxation Status',
          field: 'tax_status',
          values: [
            '',
            'CIS Gross',
            'CIS 20%',
            'CIS 30%',
            'Self-employed Gross',
            'PAYE Gross',
            'Umbrella Gross',
          ],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'Limited Company',
          field: 'is_limited_company',
          values: ['', 'Yes', 'No'],
          defaultValue: '',
        },
        {
          type: 'select',
          multiple: false,
          label: 'VAT Registered',
          field: 'vat_registered',
          values: ['', 'VAT Registered', 'Not VAT Registered'],
          defaultValue: '',
        },
      ],
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      flex: 1.5,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
          {/* The grid sets the cell's line-height to the row height; reset it so the pill fits under the name. */}
          <Box display="flex" flexDirection="column" gridGap={2} minWidth={0} lineHeight="normal">
            <AppLink
              target="_blank"
              to={`/payroll/users/${params.row?.first_name}_${params.row?.user_id}_${companyId}`}>
              {params.row.first_name} {params.row.last_name}
            </AppLink>
            {params.row.vat_treatment === VAT_TREATMENTS.REVERSE_CHARGE && <ReverseChargePill />}
          </Box>
          <Hint
            icon={
              params.row?.verification?.status === false ? (
                <BiError color={theme.palette.error.main} />
              ) : (
                <BsCheck
                  color={params.row?.on_hold ? theme.palette.warning.main : theme.palette.success.main}
                />
              )
            }>
            {params.row.verification?.errors?.length || params.row?.on_hold ? (
              <Box display="flex" flexDirection="column">
                <Typography variant="button" style={{ color: theme.palette.warning.main }}>
                  On Hold
                </Typography>
                {params.row.verification?.errors?.map((error: any) => (
                  <Typography
                    style={{ color: theme.palette.error.main, lineHeight: '32px' }}
                    variant="caption"
                    key={error.message}>
                    {error.message}
                  </Typography>
                ))}
              </Box>
            ) : undefined}
          </Hint>
        </Box>
      ),
      minWidth: 160,
    },
    {
      field: 'ni_number',
      headerName: 'Ref #',
      flex: 0.6,
      disableColumnMenu: true,
      minWidth: 90,
    },
    {
      field: 'tax_status',
      headerName: 'Tax Status',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 110,
    },
    {
      field: 'total_pay',
      headerName: 'Total Pay',
      valueFormatter: params => currencyFormatter.format(params.row.total_pay),
      flex: 0.8,
      disableColumnMenu: true,
      minWidth: 100,
    },
    {
      field: 'total_tax',
      headerName: 'TAX',
      valueFormatter: params => currencyFormatter.format(params.row.total_tax),
      flex: 0.5,
      editable: false,
      disableColumnMenu: true,
      minWidth: 80,
    },
    {
      field: 'payroll_fee',
      headerName: 'Deductions',
      valueFormatter: params => currencyFormatter.format(params.row.payroll_fee),
      flex: 0.7,
      editable: false,
      disableColumnMenu: true,
      minWidth: 120,
    },
    {
      field: 'additions',
      headerName: 'Additions',
      valueFormatter: params => currencyFormatter.format(params.row.additions),
      flex: 0.7,
      editable: false,
      disableColumnMenu: true,
      minWidth: 110,
    },
    {
      // `field` is the sort key the server receives, and `vat` is the paid half; the cell shows
      // total_vat (paid + unpaid). Left as it is — renaming it would change the sort key.
      field: 'vat',
      headerName: 'VAT (if applicable)',
      // A reverse-charged line belongs in the DRC column only — this one shows what was paid,
      // which is nothing. Branching on the treatment, never on the amount being zero.
      renderCell: params => (
        <VatAmountCell vat={params.row.total_vat} treatment={params.row.vat_treatment} />
      ),
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      minWidth: 160,
    },
    // Withheld rather than paid, so it sits outside Take Home. Only rendered once the rows
    // actually carry a treatment — see the note on PayrunLine. Like the VAT column, the cell shows
    // the paid and unpaid halves together; `drc_vat` alone is £0.00 until the run is paid.
    ...(hasReverseCharge
      ? [
          {
            field: 'drc_vat',
            headerName: 'DRC VAT',
            renderCell: (params: any) => (
              <DRCVatCell
                drcVat={sumDrcVat(params.row.drc_vat, params.row.drc_vat_unpaid)}
                treatment={params.row.vat_treatment}
                vatRate={params.row.vat_rate}
              />
            ),
            flex: 1,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            minWidth: 130,
          },
        ]
      : []),
    {
      field: 'total_net',
      headerName: 'Take Home',
      valueFormatter: params => currencyFormatter.format(params.row.total_net),
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      minWidth: 120,
    },
    {
      field: 'net',
      headerName: 'Paid',
      valueFormatter: params => currencyFormatter.format(params.row.net),
      flex: 0.7,
      editable: false,
      disableColumnMenu: true,
      minWidth: 100,
    },
    {
      field: 'net_unpaid',
      headerName: 'Outstanding',
      valueFormatter: params => currencyFormatter.format(params.row.net_unpaid),
      flex: 0.7,
      disableColumnMenu: true,
      editable: false,
      minWidth: 120,
    },
    {
      field: 'sms_notified',
      headerName: 'SMS Sent',
      flex: 0.2,
      editable: false,
      renderCell: params => <IsVerified verified={params.row.sms_notified} />,
      minWidth: 90,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'email_notified',
      headerName: 'Email Sent',
      flex: 0.2,
      editable: false,
      renderCell: params => <IsVerified verified={params.row.email_notified} />,
      minWidth: 90,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: '',
      headerName: 'Actions',
      width: 80,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%">
          <GridActions close={closeGridAction}>
            <MenuList>
              {/* Paying a worker and sending their statement are the same operations
                  as the page-level buttons, one row at a time — same two questions.
                  The document requests below are not payroll operations. */}
              {canSubmitPayRun && (
                <MenuItem
                  onClick={() => {
                    resetCloseStatus();
                    markAsPaidForEmployee(params.row.employee_id);
                  }}>
                  Mark as paid
                </MenuItem>
              )}
              {canSendPayslips && (
                <MenuItem
                  onClick={() => {
                    resetCloseStatus();
                    sendPayslipsByEmail(params.row.uuid);
                  }}>
                  Send pay statement to the worker
                </MenuItem>
              )}
              {canSendPayslips && (
                <MenuItem
                  onClick={() => {
                    resetCloseStatus();
                    sendNetPayText(params.row.uuid);
                  }}>
                  Send net pay text message to worker
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  resetCloseStatus();
                  sendPassport(params.row.user_id);
                }}>
                Send email request for a clearer ID
              </MenuItem>
              <MenuItem
                onClick={() => {
                  resetCloseStatus();
                  sendShareCode(params.row.user_id);
                }}>
                Send email request for valid Share Code
              </MenuItem>
            </MenuList>
          </GridActions>
        </Box>
      ),
    },
  ];

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const sendSMS = async () => {
    try {
      const res = await sendPayslipsToEmployeesBySMSRequest({ token, companyId, payrunId });
      showNotification({
        message: sendingMessage(res.data.lines_pending),
        options: { variant: 'success' },
      });
      setOpenSendPayStatementsDialog(false);
    } catch (e) {
      handleServerError(e);
    }
  };

  const sendEmail = async () => {
    try {
      const res = await sendPayslipsToEmployeesByEmailRequest({ token, companyId, payrunId });
      showNotification({
        message: sendingMessage(res.data.lines_pending),
        options: { variant: 'success' },
      });
      setOpenSendPayStatementsDialog(false);
    } catch (e) {
      handleServerError(e);
    }
  };

  const getPayrun = useCallback(
    async (companyId: string, payrunId: string) => {
      try {
        const res: AxiosResponse = await getPayrunRequest({
          token,
          companyId,
          payrunId,
          data: {
            limit: GRID_PAGE_SIZE,
            substring,
            offset,
            order_by: formatSortModel<PayrunLine>(sortModel),
            ...prepareFilters(filters),
          },
        });
        const resData = res.data;

        setTotals(resData.totals);
        setHovers(resData.hovers);
        setTotal(resData.pay_run_lines?.count);
        setStatus(resData.status);
        setPeriodData({
          end_date: resData.end_date,
          start_date: resData.start_date,
          tax_week_end: resData.tax_week_end,
          tax_year_end: resData.tax_year_end,
        });
        setPaySchedule(resData.pay_schedule);
        setCompany(resData.company);
        setRows(resData.pay_run_lines?.items);
        setLoading(false);
      } catch (e) {
        handleServerError(e);
      } finally {
        setLoading(false);
      }
    },
    [filters, offset, sortModel, substring, token]
  );

  useEffect(() => {
    if (!mountedRef.current) return;
    const payrunUrlItems = getUrlItems(id);
    const payrunId = payrunUrlItems?.id;
    const companyId = payrunUrlItems?.additionalId;
    setCompanyId(companyId);
    setPayrunId(payrunId);
    getPayrun(companyId, payrunId);

    return () => {
      mountedRef.current = false;
    };
  }, [id, getPayrun]);

  const refresh = async () => {
    if (!companyId || !payrunId || !paySchedule?.uuid) {
      return;
    }
    try {
      const res = await refreshPayrunRequest(token, companyId, paySchedule.uuid, payrunId);
      showNotification({ message: res.data.message, options: { variant: 'success' } });
      getPayrun(companyId, payrunId);
    } catch (e) {
      handleServerError(e);
    }
  };

  const markAsPaid = async () => {
    if (!companyId || !payrunId || !paySchedule?.uuid) {
      return;
    }
    try {
      setMarkingAsPaid(true);
      const res = await markPayrunAsPaidRequest({
        token,
        companyId,
        payScheduleId: paySchedule.uuid,
        payrunId,
      });
      showNotification({ message: res.data.message, options: { variant: 'success' } });
      setOpenConfirmDialog(false);
      getPayrun(companyId, payrunId);
    } catch (e) {
      handleServerError(e);
    } finally {
      setMarkingAsPaid(false);
    }
  };

  const markAsPaidForEmployee = async (employeeId: string) => {
    if (!companyId || !payrunId || !paySchedule?.uuid) {
      return;
    }
    try {
      const res = await markPayrunAsPaidForEmployeeRequest({
        token,
        companyId,
        payScheduleId: paySchedule.uuid,
        payrunId,
        employeeId,
      });
      showNotification({ message: res.data.message, options: { variant: 'success' } });
      getPayrun(companyId, payrunId);
    } catch (e) {
      handleServerError(e);
    }
  };

  const sendPayslipsByEmail = async (payrunLineId: string) => {
    if (!companyId || !payrunId) {
      return;
    }
    try {
      const res = await sendPayslipsByEmailRequest({
        token,
        companyId,
        payrunLineId,
      });
      showNotification({
        message: sendingMessage(res.data.lines_pending),
        options: { variant: 'success' },
      });
    } catch (e) {
      handleServerError(e);
    }
  };

  const sendNetPayText = async (payrunLineId: string) => {
    if (!companyId || !payrunId) {
      return;
    }
    try {
      const res = await sendPayslipsBySMSRequest({
        token,
        companyId,
        payrunLineId,
      });
      showNotification({
        message: sendingMessage(res.data.lines_pending),
        options: { variant: 'success' },
      });
    } catch (e) {
      handleServerError(e);
    }
  };

  const sendPassport = async (userId: string) => {
    try {
      const res = await sendPassportRequest({
        token,
        companyId,
        userId,
      });
      showNotification({ message: res.data.message, options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    }
  };

  const sendShareCode = async (userId: string) => {
    try {
      const res = await sendShareCodeRequest({
        token,
        companyId,
        userId,
      });
      showNotification({ message: res.data.message, options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const prepareFilters = (filters: any): PayrunPayload => {
    return {
      id_verified: getBooleanFilterValue(filters?.id_verified),
      cis_verified: getBooleanFilterValue(filters?.cis_verified),
      vat_registered: getBooleanFilterValue(filters?.vat_registered),
      business_types: getBusinessTypesFilterValue(filters?.is_limited_company),
      rtw_status: filters?.rtw_status?.value || undefined,
      id_status: filters?.id_status || undefined,
      pay_scheme: filters?.pay_scheme.toLowerCase() || undefined,
      tax_status: filters?.tax_status || undefined,
    };
  };

  const exportPayrun = async () => {
    if (!companyId || !payrunId) {
      return;
    }
    try {
      const res = await getPayrunAsFileRequest(token, companyId, payrunId);
      // The employer export is on the gateway and answers a presigned link; the Legend one is
      // still on the legacy blob route (see getPayrunAsFileRequest in api/legend/pay.ts).
      if (res.data?.url) {
        openPresignedFile(res.data);
      } else {
        generateExcelFileLink(res);
      }
    } catch (e) {
      handleServerError(e);
    }
  };

  const options = useMemo(
    () => [
      {
        title: 'Refresh',
        icon: <AiOutlineRedo size="15px" color={theme.palette.primary.main} />,
        disabled: loading || isPaid,
        onClick: refresh,
      },
      {
        title: 'Export',
        icon: <BiExport size="15px" color={theme.palette.primary.main} />,
        disabled: loading || !rows?.length,
        onClick: exportPayrun,
      },
      // The mobile menu mirrors the desktop buttons, so it takes the same gates.
      ...(canSendPayslips
        ? [
            {
              title: 'Send Pay Statements',
              icon: <FiSend size="15px" color={theme.palette.primary.main} />,
              disabled: loading || !rows?.length,
              onClick: () => setOpenSendPayStatementsDialog(true),
            },
          ]
        : []),
      ...(canSubmitPayRun
        ? [
            {
              title: 'Mark As Paid',
              icon: <BsCheck size="20px" color={theme.palette.primary.main} />,
              disabled: loading || isPaid || !rows?.length,
              cy: 'open-mark-as-paid-dialog',
              onClick: () => setOpenConfirmDialog(true),
            },
          ]
        : []),
    ],
    [exportPayrun, isPaid, loading, refresh, rows?.length, canSendPayslips, canSubmitPayRun]
  );

  const downloadBACSButton = useMemo(
    () => (
      <DownloadBACS
        downloadPayrunBACSRequest={downloadPayrunBACSRequest}
        downloadPayrunsBACSRequest={downloadPayrunsBACSRequest}
        mode="single"
        disabled={!rows?.length}
        payrunId={payrunId}
        // Legend Interface (for Vyce outsourced payruns)
        companyId={paySchedule?.outsourced ? 'vyce' : companyId}
      />
    ),
    [
      companyId,
      downloadPayrunBACSRequest,
      downloadPayrunsBACSRequest,
      paySchedule?.outsourced,
      payrunId,
      rows?.length,
    ]
  );

  useEffect(() => {
    if (!companyId || !payrunId) {
      return;
    }
    getPayrun(companyId, payrunId);
  }, [getPayrun, companyId, payrunId]);

  const mobileControlView = useMemo(
    () => (
      <Box
        display="flex"
        alignItems="center"
        gridGap={16}
        margin={isMobile ? '40px 0 12px 0' : '24px 0'}
        justifyContent="space-between"
        className={clsx(horizontalScrollStyles.blockWithHideScroll, horizontalScrollStyles.blockWrapper)}>
        <Box display="flex" alignItems="center" flex={1}>
          <Box width="100%">
            <AppSearchInput onChange={handleSearchChange} isSmall expanded={true} fullWidth={true} />
          </Box>
        </Box>
        <Box display="flex" gridGap={8}>
          {payrunId && companyId && downloadBACSButton}

          <FilterSystem
            buttonBorder
            filtersSections={filtersSections}
            onFiltersChange={handleFilterChange}
          />
          <AppMenu options={options} cy="open-actions-dropdown-menu" />
        </Box>
      </Box>
    ),
    [
      companyId,
      downloadBACSButton,
      filtersSections,
      handleFilterChange,
      handleSearchChange,
      options,
      payrunId,
      isMobile,
    ]
  );

  const desktopControlView = useMemo(
    () => (
      <Box
        margin={'24px 0'}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gridGap={8}
        justifyContent="space-between">
        <Box display="flex" gridGap={8}>
          <AppSearchInput onChange={handleSearchChange} isSmall expanded={true} />

          <AppIconButton disabled={loading || isPaid} variant="primary" onClick={refresh}>
            <AiOutlineRedo color={theme.palette.text.primary} />
          </AppIconButton>

          <Button
            onClick={exportPayrun}
            disabled={loading || !rows?.length}
            startIcon={<BiExport size="15px" />}
            variant="contained"
            size="small"
            color="secondary">
            Export
          </Button>
        </Box>

        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {canSendPayslips && (
            <Button
              onClick={() => setOpenSendPayStatementsDialog(true)}
              disabled={loading || !rows?.length}
              startIcon={<FiSend size="16px" />}
              variant="contained"
              size="small"
              color="primary">
              Send Pay Statements
            </Button>
          )}

          {canSubmitPayRun && (
            <Button
              onClick={() => setOpenConfirmDialog(true)}
              disabled={loading || isPaid || !rows?.length}
              startIcon={<BsCheck />}
              variant="contained"
              size="small"
              cy-test-id="open-mark-as-paid-dialog"
              color="primary">
              Mark As Paid
            </Button>
          )}

          {canGenerateBacs && payrunId && companyId && downloadBACSButton}

          <FilterSystem
            buttonBorder
            filtersSections={filtersSections}
            onFiltersChange={handleFilterChange}
          />
        </Box>
      </Box>
    ),
    [
      companyId,
      downloadPayrunBACSRequest,
      downloadPayrunsBACSRequest,
      exportPayrun,
      filtersSections,
      handleFilterChange,
      handleSearchChange,
      isPaid,
      loading,
      paySchedule?.outsourced,
      payrunId,
      refresh,
      rows?.length,
      token,
      canSendPayslips,
      canSubmitPayRun,
      canGenerateBacs,
    ]
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <PayScheduleInfo
            periodData={periodData}
            paySchedule={paySchedule}
            companyName={company?.name}
            status={status}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper className={classes.wrapper} variant="outlined">
            {totals && (
              <Box alignItems={isMobile ? 'flex-start' : 'flex-end'} display="flex" flexDirection="column">
                <Box marginBottom={1}>
                  <Tooltip title={hovers?.totals?.total_gross ?? ''} placement="top">
                    <Typography variant="subtitle2" className={clsx(classes.bold, classes.smallText)}>
                      Gross: {currencyFormatter.format(totals?.total_gross)}
                    </Typography>
                  </Tooltip>
                </Box>
                <Tooltip title={hovers?.totals?.total_net ?? ''} placement="top">
                  <Typography variant="subtitle2" className={classes.smallText}>
                    Net: {currencyFormatter.format(totals?.total_net)}
                  </Typography>
                </Tooltip>
                {/* The run's reverse-charge liability — shown but not paid out. */}
                {runDrcVat !== undefined && (
                  <Typography variant="subtitle2" className={classes.smallText} color="primary">
                    Reverse charge VAT: {currencyFormatter.format(runDrcVat)}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {isDesktop ? desktopControlView : mobileControlView}

      <Box marginTop={2}>
        <AppDataGrid
          rows={rows}
          rowCount={total}
          height="calc(100vh - 420px)"
          getRowId={row => row.uuid}
          columns={applyColumnHovers(columns, hovers?.lines, { vat: 'total_vat', drc_vat: 'total_drc_vat' })}
          loading={loading}
          pageSize={GRID_PAGE_SIZE}
          rowsPerPageOptions={[GRID_PAGE_SIZE]}
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          disableSelectionOnClick
          unit="payruns"
        />

        <ConfirmDialog
          handleClose={() => setOpenConfirmDialog(false)}
          open={openConfirmDialog}
          loading={markingAsPaid}
          buttonMinWidth={150}
          confirmText="Mark As Paid"
          cancelText="Cancel"
          subtitle="All individuals that are on hold because they do not have verified ID or RTW will not be marked as paid. You can mark them as paid once their ID and RTW have been verified."
          title="Mark As Paid?"
          handleConfirm={markAsPaid}
        />

        <SendPayStatementsDialog
          sendSMS={sendSMS}
          sendEmail={sendEmail}
          open={openSendPayStatementsDialog}
          setOpen={setOpenSendPayStatementsDialog}
        />
      </Box>
    </>
  );
};
